import type { StatusDefinitionRow } from '../statusDefinitions';

/**
 * The request for quotation (piece 22; decision 0121, built ahead of the rest
 * of the piece by decision 0316): the two status vocabularies, the rows the
 * seed mirrors, and the shapes the Deal page's RFQ modal and the API exchange.
 */
export const RFQ_STATUSES = ['Draft', 'Issued', 'ResponsesIn', 'Awarded', 'Cancelled'] as const;
export type RfqStatus = (typeof RFQ_STATUSES)[number];
export const RFQ_RESPONSE_STATUSES = ['Sent', 'Received', 'Declined', 'Expired'] as const;
export type RfqResponseStatus = (typeof RFQ_RESPONSE_STATUSES)[number];

/** One StatusDefinition row per member: Sent is a wait, Declined a refusal, Cancelled and Expired inactive, the rest good. */
export const RFQ_STATUS_DEFINITIONS: readonly StatusDefinitionRow[] = [
  ...RFQ_STATUSES.map((value, i): StatusDefinitionRow => ({
    entityType: 'RFQStatus', statusValue: value, label: value === 'ResponsesIn' ? 'Responses In' : value === 'Cancelled' ? 'Canceled' : value,
    colorGroup: value === 'Draft' || value === 'Cancelled' ? 'inactive' : 'good', sortOrder: i + 1,
  })),
  ...RFQ_RESPONSE_STATUSES.map((value, i): StatusDefinitionRow => ({
    entityType: 'RFQResponseStatus', statusValue: value, label: value,
    colorGroup: value === 'Sent' ? 'warning' : value === 'Declined' ? 'error' : value === 'Expired' ? 'inactive' : 'good', sortOrder: i + 1,
  })),
];

/** A vendor the modal offers: the company, the BOM lines it is suggested for, and the people there with a work email. */
export interface RfqVendorRow {
  companyId: string;
  displayName: string;
  /** Lines whose BOMLine names this vendor, whose Item has an active ItemSupplier row for it, whose category or an ancestor the vendor reps, or which the vendor supplied on a prior Purchase Order (piece 22, the union). */
  suggestedLineIds: string[];
  /** Which list suggested each line, keyed by BOM line id, so the picker can say why (built 2026-09-14). */
  suggestionSources?: Record<string, string[]>;
  contacts: { employmentId: string; name: string; email: string }[];
  /** 🔒 The vendor's open Sales mailbox (decision 2974), which the modal opens on, typed and changeable, ahead of the first contact. */
  salesMailbox?: string | null;
}

/** One BOM line as the modal lists it for ticking. */
export interface RfqLineChoice {
  bomLineId: string;
  sku: string;
  itemName: string;
  manufacturerPartNumber: string | null;
  description: string | null;
  quantity: number;
  unitName: string;
  vendorId: string | null;
}

export interface RfqSendRequest {
  vendorCompanyId: string;
  /** The vendor contact the email goes to, or a typed address when the vendor has no contact with a work email. */
  contactEmploymentId?: string | null;
  email?: string | null;
  bomLineIds: string[];
  /** A calendar date, YYYY-MM-DD, in the organization's zone. */
  responsesDueOn?: string | null;
  message?: string | null;
  /** 🔨 A ticked line no vendor covers blocks the issue until the person acknowledges the unassigned section (piece 22). */
  acknowledgeUnassigned?: boolean;
  /** 🔨 A vendor already asked about one of these lines on this Deal blocks the send until the person confirms the repeat (decision 0499). */
  confirmRepeat?: boolean;
  /** The RFQ's own chase period and alarm, in working days; blank reads the Settings defaults. */
  chaseEveryWorkingDays?: number | null;
  noResponseAlarmWorkingDays?: number | null;
}

export interface RfqSentAnswer {
  rfqId: string;
  rfqNumber: string;
  vendorName: string;
  to: string;
  /** What the Outbound Mail Service reported; a Failed send still leaves the RFQ issued, with the failure in the queue. */
  send: 'Accepted' | 'Deferred' | 'Rejected' | 'Failed';
  lineCount: number;
}

/** The file names the email carries and the downloads answer, from the number alone. */
export function rfqFileName(rfqNumber: string, kind: 'pdf' | 'xlsx'): string {
  return `${rfqNumber.replace(/[^A-Za-z0-9_-]+/g, '_')}.${kind}`;
}
